/**
 * Solo subscription view
 *
 * Single-page experience for `organization_info.group === false` orgs.
 * Combines tier selection, add-on selection, invite-member CTA, cancel
 * section, and a sticky `ManagementSidebar` showing cost + payment + cycle.
 *
 * Live cost recalculation as the user toggles selections; the sidebar
 * surfaces a "Updated Cost" preview + Confirm/Revert when changes pend.
 */

import { useState } from 'react';
import { InviteUserDialog } from '@features/organizations/components/InviteUserDialog';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import type { FullSubscriptionDetailsResponse } from '@store/generated/subscriptions';

import { useGetInvoicesQuery } from '../../store/api';
import { useBundleCatalog } from '../../store/hooks';
import { getAddOnsIncludedInTier } from '../../utils';
import { BillingIntervalToggle } from '../BillingIntervalToggle';
import { CancelSubscriptionSection } from '../CancelSubscriptionSection';
import { InvoiceHistoryModal } from '../InvoiceHistoryModal';
import { ManagementAddOnCard } from '../ManagementAddOnCard';
import { ManagementSidebar } from '../ManagementSidebar';
import { ManagementTierCard } from '../ManagementTierCard';

import { useSoloSubscription } from './useSoloSubscription';

const INVOICE_FETCH_LIMIT = 3;

interface SoloSubscriptionViewProps {
  subscription: FullSubscriptionDetailsResponse;
  onUpdatePaymentMethod: () => void;
  onSubscriptionStatusChange: () => void;
}

export function SoloSubscriptionView({
  subscription,
  onUpdatePaymentMethod,
  onSubscriptionStatusChange,
}: SoloSubscriptionViewProps) {
  const { subscriptionManagement } = useEventTracking();
  const {
    tiers,
    addOnsForDisplay,
    annualDiscountRange,
    isLoading: isCatalogLoading,
  } = useBundleCatalog();
  const { data: invoicesData } = useGetInvoicesQuery({ limit: INVOICE_FETCH_LIMIT });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [invoiceHistoryOpen, setInvoiceHistoryOpen] = useState(false);

  const card = useSoloSubscription({
    subscription,
    tiers,
    addOns: addOnsForDisplay,
    onCommitted: onSubscriptionStatusChange,
  });

  const isTrialing = subscription.is_trialing ?? false;
  const isAnnualBilling = card.effectiveInterval === 'year';
  const hasNoTier = !card.effectiveTierId;

  const handleInviteOpen = () => {
    subscriptionManagement.soloInviteCtaClicked();
    setInviteOpen(true);
  };

  const handleShowAllInvoices = () => {
    subscriptionManagement.invoiceHistoryOpened({ org_type: 'solo' });
    setInvoiceHistoryOpen(true);
  };

  const handleCloseInvoiceHistory = () => {
    subscriptionManagement.invoiceHistoryClosed({ org_type: 'solo' });
    setInvoiceHistoryOpen(false);
  };

  return (
    // MUI Grid container so we can place the danger zone in its own row —
    // at lg+ it slots under the main content in the left column, keeping the
    // sticky sidebar uninterrupted; on narrow viewports it stacks last
    // (main → sidebar → danger zone) instead of getting buried below
    // the cancel section. Mirrors `GroupSubscriptionOverview`'s layout.
    //
    // No `alignItems` override — MUI Grid defaults to `align-items: stretch`,
    // which makes the sidebar's column as tall as the main column. That extra
    // height is what `position: sticky` needs to activate (the sticky element
    // scrolls within its containing block; if the block collapses to the
    // card's own height, sticky never engages).
    <Grid container spacing={3}>
      {/* ═══ MAIN: Invite CTA + Tier + Add-ons ═══ */}
      <Grid size={{ xs: 12, lg: 8 }}>
        {/* Invite Member CTA — prominent, at the top */}
        <Box
          sx={theme => ({
            p: 2,
            borderRadius: 1,
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          })}
        >
          <Box>
            <Typography variant="subtitle2">Add a team member</Typography>
            <Typography variant="body2" color="text.secondary">
              Invite a clinician or staff member to your practice.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PersonAddIcon />}
            onClick={handleInviteOpen}
          >
            Invite Member
          </Button>
        </Box>

        {/* Tier section */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1.5,
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="h6">{hasNoTier ? 'Choose a Tier' : 'Your Tier'}</Typography>
            {/* Billing-interval toggle mirrors the GroupSubscriptionOverview
                position so both surfaces feel like the same control. Preview
                mode becomes a pending change in the sidebar; Confirm fires the
                bundle mutations + updateBillingInterval atomically. */}
            <BillingIntervalToggle
              value={card.effectiveInterval}
              onChange={card.setBillingInterval}
              size="small"
              discountRange={annualDiscountRange}
              disabled={card.isCommitting}
            />
          </Box>
          {isCatalogLoading ? (
            <Grid container spacing={2}>
              {[0, 1, 2].map(i => (
                <Grid size={{ xs: 12, md: 4 }} key={i}>
                  <Skeleton variant="rounded" sx={{ height: 220 }} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={2}>
              {tiers.map(tier => (
                <Grid size={{ xs: 12, md: 4 }} key={tier.bundle_id}>
                  <ManagementTierCard
                    bundle={tier}
                    billingInterval={isAnnualBilling ? 'year' : 'month'}
                    includedAddons={getAddOnsIncludedInTier(tier, addOnsForDisplay)}
                    isActive={tier.bundle_id === card.effectiveTierId}
                    isCurrent={tier.bundle_id === card.currentTierId}
                    onSelect={card.setTier}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Add-ons section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Add-ons
          </Typography>
          {isCatalogLoading ? (
            <Grid container spacing={2}>
              {[0, 1, 2].map(i => (
                <Grid size={{ xs: 12, md: 4 }} key={i}>
                  <Skeleton variant="rounded" sx={{ height: 140 }} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={2}>
              {addOnsForDisplay.map(addOn => {
                const isCovered = card.tierCoveredAddOnIds.has(addOn.bundle_id);
                const isSelected = card.effectiveAddOnIds.has(addOn.bundle_id);
                const isActive = isSelected || isCovered;
                // Status chip wording mirrors the prototype's Solo addon card:
                // "Included" (tier covers it) > "Active" (user selected) > "Available"
                const chipLabel = isCovered
                  ? 'Included'
                  : isSelected
                    ? 'Active'
                    : 'Available';
                return (
                  <Grid size={{ xs: 12, md: 4 }} key={addOn.bundle_id}>
                    <ManagementAddOnCard
                      bundle={addOn}
                      billingInterval={isAnnualBilling ? 'year' : 'month'}
                      isActive={isActive}
                      isIncluded={isCovered}
                      statusChip={{
                        label: chipLabel,
                        color: isActive ? 'primary' : 'default',
                      }}
                      onToggle={card.toggleAddOn}
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}
          {hasNoTier && (
            <Alert severity="info" sx={{ mt: 2, fontSize: '0.85rem' }}>
              Select a tier above to enable add-ons.
            </Alert>
          )}
        </Box>
      </Grid>

      {/* ═══ SIDEBAR: ManagementSidebar ═══
          At lg+ this sits beside the main content; on narrow viewports it
          stacks between the main content and the danger zone. */}
      <Grid size={{ xs: 12, lg: 4 }}>
        <ManagementSidebar
          subscription={subscription}
          invoices={invoicesData?.invoices ?? []}
          onUpdatePaymentMethod={onUpdatePaymentMethod}
          onShowAllInvoices={handleShowAllInvoices}
          pendingChange={
            card.hasChanges
              ? {
                  newMonthlyCents: card.projectedCost.cents,
                  // Only signal the interval to the sidebar when it's actually
                  // changing — passing the same value would still flip the
                  // sidebar into "preview" mode unnecessarily.
                  newBillingInterval:
                    card.effectiveInterval !== card.currentBillingInterval
                      ? card.effectiveInterval
                      : undefined,
                  onConfirm: card.commit,
                  onRevert: card.revert,
                  isConfirming: card.isCommitting,
                }
              : undefined
          }
        />
      </Grid>

      {/* ═══ DANGER ZONE: Cancel subscription ═══
          Own Grid row so on narrow viewports it lands below the sidebar
          (main → sidebar → danger) instead of getting buried beside it. At
          lg+ it occupies the left column on a second row, leaving the sticky
          sidebar uninterrupted. */}
      <Grid size={{ xs: 12, lg: 8 }}>
        <Divider sx={{ mb: 3 }} />
        <CancelSubscriptionSection
          cancelAtPeriodEnd={subscription.cancel_at_period_end ?? undefined}
          currentPeriodEnd={subscription.current_period_end}
          subscriptionStatus={subscription.subscription_status}
          onStatusChange={onSubscriptionStatusChange}
        />
      </Grid>

      <InviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={() => {
          setInviteOpen(false);
          onSubscriptionStatusChange();
        }}
        isTrialing={isTrialing}
      />

      <InvoiceHistoryModal open={invoiceHistoryOpen} onClose={handleCloseInvoiceHistory} />
    </Grid>
  );
}
